import memoize from '@emotion/memoize'
import { type AxiosError, isAxiosError } from 'axios'
import DOMPurify from 'dompurify'
import { isEmpty } from 'lodash'

import { instanceOfError } from 'utils/errors'

export const capitalize = (text: string): string =>
  `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`

export function pluralize(baseStr: string, count: number): string {
  if (count === 1) return baseStr

  // handling use case when last letter ends with a y
  const vowels = ['a', 'e', 'i', 'o', 'u']

  // when the word has a consonant (non-vowel) before the letter y, we
  // need to remove the y and replace it with `ies`
  if (!vowels.includes(baseStr.slice(-2, -1)) && baseStr.slice(-1) === 'y') {
    return `${baseStr.slice(0, baseStr.length - 1)}ies`
  }

  return `${baseStr}s`
}

export function prettyEnumConstant(stringConstant: string): string {
  return stringConstant.split('_').map(capitalize).join(' ')
}

/**
 * Unescape HTML entities
 * Use with caution!! React will encode the values again for display to prevent
 * XSS attacks, but if using outside of jsx use caution.
 * @param str
 * @returns {string}
 */
export const unescapeHTMLEntities = memoize((str: string) => {
  const placeholder = document.createElement('textarea')
  placeholder.innerHTML = str

  return placeholder.childNodes[0]?.nodeValue ?? ''
})

export const sanitize = memoize((str) => DOMPurify.sanitize(str))

/**
 * Get error message from api response error
 * @param err Error from response
 * @param fallbackMessage Fallback message if there is no error response
 * @param overrideStatusCodes If the status code matches this array, still use the default message
 * @returns {string}
 */
export function messageFromResponseError(
  err: AxiosError | undefined | null,
  fallbackMessage = '',
  overrideStatusCodes: Record<number, string> = {}
): string {
  const overrideStatusCodeMessage =
    err?.response?.status && overrideStatusCodes[err?.response?.status]

  if (overrideStatusCodeMessage) {
    return overrideStatusCodeMessage
  }

  if (err?.response?.data && typeof err.response.data === 'string') {
    // some errors from the backend contain the '<' character, which gets escaped by AxiosError
    // unescape those strings here so we can display clear error messages
    const unescaped: string | null = unescapeHTMLEntities(err.response.data)
    if (unescaped) {
      return unescaped
    }
  }

  return fallbackMessage
}

export function getMessageFromResponseOrError(
  err: unknown,
  fallbackMessage = ''
): string {
  if (isAxiosError(err)) {
    return messageFromResponseError(err, fallbackMessage)
  } else if (instanceOfError(err)) {
    return (err as Error).message
  } else if (typeof err === 'string') {
    return err
  } else {
    return fallbackMessage
  }
}

export const isValidUrl = (url: string | undefined): boolean => {
  if (!url) return false

  if (parseUrl(url)) {
    return true
  }

  return false
}

/**
 * Parses a url and returns the parse value or false if it's invalid
 */
export function parseUrl(url?: string) {
  if (!url) return false

  try {
    const parsedUrl = new URL(url)
    return parsedUrl
  } catch {
    return false
  }
}

/**
 * strips an array of text from a string
 * @param {string} text
 * @param {Array} stripTextArray
 */
export function stripText(text: string, stripTextArray: string[] = []): string {
  if (isEmpty(stripTextArray)) {
    return text
  }

  let newText = text
  stripTextArray.forEach((textToStrip: string) => {
    newText = newText.replace(textToStrip, '').trim()
  })

  return newText
}

/**
 * escapeStringForPosix
 *
 * Escape string for use in linux based command line
 * https://github.com/jmealo/node-request-to-curl - MIT License
 *
 * @param {string} str
 */
export function escapeStringForPosix(str: string): string {
  function escapeCharacter(x) {
    let code = x.charCodeAt(0)
    if (code < 256) {
      // Add leading zero when needed to not care about the next character.
      return code < 16 ? `\\x0${code.toString(16)}` : `\\x${code.toString(16)}`
    }
    code = code.toString(16)
    return `\\u${`0000${code}`.substr(code.length, 4)}`
  }

  if (/[^\x20-\x7E]|'/.test(str)) {
    // Use ANSI-C quoting syntax.
    return `$'${str
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/[^\x20-\x7E]/g, escapeCharacter)}'`
  } else {
    // Use single quote syntax.
    return `'${str}'`
  }
}

export const urlRegex =
  /((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#-]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/gi
export const uuidRegex =
  /^[A-F\d]{8}-[A-F\d]{4}-[A-F\d]{4}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i

export const isValidId = (maybeId: string): boolean => {
  if (typeof maybeId !== 'string') {
    return false
  }
  return uuidRegex.test(maybeId)
}

export function convertKebabCaseToCamelCase(kebabString?: string) {
  return kebabString?.replace(/-./g, (x) => {
    const secondChar = x[1] ?? ''
    return secondChar.toUpperCase()
  })
}

export function convertCamelCaseToKebabCase(camelString?: string) {
  return camelString?.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

export function separateCamelCaseWords(str?: string) {
  return str?.split(/(?=[A-Z])/)
}

/**
 * Takes any possible redirect url and filters it through the URL object to reduce it to just the path
 * This is used to filter login redirect urls so we don't blindly accept any redirect URL
 * Defaults to returning '/' if there are any problems with the received url
 * @param path
 */
export const getFilteredRedirectPath = (path = '') => {
  try {
    // for relative urls, we prepend the gremin domain so URL handles it correctly
    if (path?.[0] === '/') {
      return new URL(path, window.location.toString()).pathname
    }

    return new URL(path).pathname
  } catch {
    return '/'
  }
}

export function getUnauthorizedMessage(endpoint: string, method: string) {
  return `Unauthorized to perform ${method.toUpperCase()} on ${endpoint}`
}

/** Filters out any falsy items before joining */
export function cleanJoin(
  itemsToJoin: Array<number | string | boolean | null | undefined>,
  separator: string
) {
  return itemsToJoin.filter(Boolean).join(separator)
}

export function isNumberStringEqual(
  val1: string | number,
  val2: string | number
): boolean {
  const val1Number = Number(val1)
  const val2Number = Number(val2)

  const val1String = String(val1)
  const val2String = String(val2)

  return val1Number === val2Number || val1String === val2String
}

/*
 * Transforms a given string from its current format to sentence case format.
 * Optionally accepts a custom delimiter used to split the string into separate words.
 * examples:
 *   - "lower case string" => "Lower Case String"
 *   - "UPPER CASE STRING" => "Upper Case String"
 *   - "MiXeD CaSe StRiNg" => "Mixed Case String"
 *   - 'kebab-case-string' => "Kebab Case String" // given '-' as a delimiter
 *   - 'snake_case_string' => "Snake Case String" // given '_' as a delimiter
 *
 * NOTE: This is a naïve implementation that doesn't consider preserving special formatted words.
 */
export function toSentenceCase(string: string, delimiter = ' ') {
  return string
    ?.split(delimiter)
    .map((word) => {
      const lowercaseWord = word.toLowerCase()
      return lowercaseWord.charAt(0).toUpperCase() + lowercaseWord.slice(1)
    })
    .join(' ')
}

export function parseCommaSeparatedValues(input: string) {
  return input
    .trim()
    .split(/\s*,\s*/)
    .filter(Boolean)
}
